import { useCallback, useContext, useMemo } from 'react';
import { DocExtension } from '@remirror/extension-doc';
import { ParagraphExtension } from '@remirror/extension-paragraph';
import { TextExtension } from '@remirror/extension-text';
import {
  EditorComponent,
  ReactComponentExtension,
  PlaceholderExtension,
  useRemirror,
  Remirror,
} from '@remirror/react';
import { Remirror as RemirrorTypes } from '@trinity/chat';
import type { InvalidContentHandler, RemirrorEventListener } from 'remirror';
import {
  BoldExtension,
  BulletListExtension,
  ItalicExtension,
  LinkExtension,
  OrderedListExtension,
  UnderlineExtension,
} from 'remirror/extensions';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming';
import { cx } from '@trinity/chat-components/utils';
import { ImageExtension } from './Extension';
import { TextColorExtension } from './Extension.TextColor';
import { LinkContainer } from './Link.Container';
import styles from './style/BasicMarkupEditor.module.css';
import { Toolbar } from './Toolbar';
import {
  BoldButton,
  ColorButton,
  ImageButton,
  ItalicButton,
  LinkButton,
  ListBulletedButton,
  ListOrderedButton,
  UnderlineButton,
} from './Toolbar.Commands';

const defaultInitialContent: RemirrorTypes.RootNode = {
  type: RemirrorTypes.NodeType.Doc,
  content: [
    {
      content: [],
      type: RemirrorTypes.NodeType.Paragraph,
    },
  ],
};

const defaultImageToolbarOptions = {
  videos: true,
};

export type BasicMarkupEditorProps = {
  className?: string;
  content?: RemirrorTypes.RootNode;
  onChange?: RemirrorEventListener<BasicMarkupEditorExtensions>;
  editing?: boolean;
  style?: React.CSSProperties;
  imageToolbarOptions?: {
    videos?: boolean;
  };
};

export function BasicMarkupEditor({
  className,
  content,
  onChange,
  editing = true,
  style = {},
  imageToolbarOptions = {},
}: BasicMarkupEditorProps) {
  const theming = useContext(AppThemingContext);

  const { manager, state } = useRemirrorEditor({
    content: content ?? defaultInitialContent,
  });

  const imageOptions = useMemo(() => {
    return {
      ...defaultImageToolbarOptions,
      ...imageToolbarOptions,
    };
  }, [imageToolbarOptions]);

  return (
    <div className={cx(styles.root)} style={{ '--theme-color': theming.enabled ? theming.palette.primary.main : 'var(--pri-02)', ...style } as React.CSSProperties}>
      <Remirror
        classNames={[styles.editor, className ?? '']}
        editable={editing}
        manager={manager}
        onChange={onChange}
        initialContent={state}
      >
        <LinkContainer>
          {editing && (
            <Toolbar>
              <BoldButton />
              <ItalicButton />
              <UnderlineButton />
              <LinkButton />
              <ImageButton {...imageOptions} />
              <ListBulletedButton />
              <ListOrderedButton />
              <ColorButton />
            </Toolbar>
          )}
          <EditorComponent />
        </LinkContainer>
      </Remirror>
    </div>
  );
}

type Params = {
  content?: RemirrorTypes.RootNode;
};

export const useRemirrorEditor = (params: Params) => {
  const onError: InvalidContentHandler = useCallback(
    ({ json, invalidContent, transformers }) => {
      console.error('Invalid Content', invalidContent);
      return transformers.remove(json, invalidContent);
    },
    [],
  );

  return useRemirror({
    content: params?.content ?? defaultInitialContent,
    extensions: () => [
      new DocExtension({}),
      new PlaceholderExtension('Start typing...'),
      new ParagraphExtension({}),
      new TextExtension(),
      new ReactComponentExtension({}),
      new BoldExtension({}),
      new ItalicExtension(),
      new UnderlineExtension(),
      new TextColorExtension({}),
      new LinkExtension({
        autoLink: true,
        defaultTarget: '_blank',
      }),
      new ImageExtension({
        enableResizing: true,
      }),
      new BulletListExtension({}),
      new OrderedListExtension({}),
    ],
    onError,
  });
};

export type BasicMarkupEditorExtensions =
  | PlaceholderExtension
  | TextExtension
  | ParagraphExtension
  | DocExtension
  | BoldExtension
  | ItalicExtension
  | UnderlineExtension
  | LinkExtension;